import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { BarChart, LineChart, StatCard } from '@/components/charts/SimpleChart'

export default function AdminAnalyticsPanel() {
  const [dateRange, setDateRange] = useState('30')

  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['analytics-summary', dateRange],
    queryFn: async () => {
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - parseInt(dateRange))

      const { data, error } = await supabase.rpc('get_analytics_summary', {
        start_date: startDate.toISOString(),
        end_date: new Date().toISOString(),
      })

      if (error) throw error
      return data?.[0] || {}
    },
  })

  const { data: auditLogs } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select(`
          *,
          user:profiles!user_id(full_name, email)
        `)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error
      return data
    },
  })

  const stats = analyticsData || {}
  const logs = auditLogs || []

  return (
    <div className="space-y-8">
      {/* Date Range Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-secondary-900">Analytics & Audit Logs</h2>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="input w-auto"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Analytics Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="text-sm text-secondary-600 mb-1">Total Events</div>
          <div className="text-3xl font-bold text-secondary-900">
            {isLoading ? '...' : (stats.total_events || 0).toLocaleString()}
          </div>
        </div>

        <div className="card p-6">
          <div className="text-sm text-secondary-600 mb-1">Unique Users</div>
          <div className="text-3xl font-bold text-primary-600">
            {isLoading ? '...' : (stats.unique_users || 0).toLocaleString()}
          </div>
        </div>

        <div className="card p-6">
          <div className="text-sm text-secondary-600 mb-1">Project Views</div>
          <div className="text-3xl font-bold text-blue-600">
            {isLoading ? '...' : (stats.project_views || 0).toLocaleString()}
          </div>
        </div>

        <div className="card p-6">
          <div className="text-sm text-secondary-600 mb-1">Reservations</div>
          <div className="text-3xl font-bold text-success-600">
            {isLoading ? '...' : (stats.unit_reservations || 0).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Popular Projects Chart */}
      {stats.popular_projects && stats.popular_projects.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-6">
            Most Viewed Projects
          </h3>
          <BarChart
            data={stats.popular_projects.slice(0, 10).map(p => ({
              label: p.title?.substring(0, 20) || 'Unknown',
              value: p.views || 0,
            }))}
            height={300}
          />
        </div>
      )}

      {/* Audit Logs */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">
          Recent Admin Actions
        </h3>

        {logs.length === 0 ? (
          <div className="text-center py-8 text-secondary-500">
            No audit logs yet
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <div
                key={log.id}
                className="flex items-start gap-4 p-3 bg-secondary-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-secondary-900">
                      {log.user?.full_name || log.user?.email || 'Unknown User'}
                    </span>
                    <span className="text-secondary-500">·</span>
                    <span className="text-sm text-secondary-600">
                      {new Date(log.created_at).toLocaleString('en-NZ', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </span>
                  </div>
                  <div className="text-sm text-secondary-700">
                    <span className="font-medium">{log.action}</span> on{' '}
                    <span className="font-medium">{log.resource_type}</span>
                  </div>
                  {log.ip_address && (
                    <div className="text-xs text-secondary-500 mt-1">
                      IP: {log.ip_address}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
